import React, { useEffect, useRef, useState } from 'react';
import {
  ItemType,
  ItemTypeCategory,
  BillingCategory,
  BillingFrequency,
  CustomFieldDefinitionInput,
  ItemTypeDefaultEventPriceInput,
  ItemTypeEventPriceCurrency,
} from '../../graphql/generatedTypes';
import { CustomModal } from '../../components/CustomModal';
import type { ItemTypeFormState } from './types';
import i18n from '@/utils/i18n';
import { useCreateItemTypeMutation } from '../../graphql/mutations/createItemType.generated';
import { LayoutStoreInstance } from '../../LayoutStore';
import { useUpdateItemTypeMutation } from '../../graphql/mutations/updateItemType.generated';
import { useSyncItemTypesToZohoMutation } from '../../graphql/mutations/syncItemTypesToZoho.generated';
import { FaPlus, FaSave, FaTrash } from 'react-icons/fa';
import { randomId } from '@/utils/random';
import { useMutation, useQuery } from '@apollo/client';
import uploadToS3 from '@/utils/imageUpload';
import { requestUploadUrl } from '../../graphql/mutations';
import { centsToPrice } from '@/utils/formatter';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { allUserCountriesQuery } from '../../graphql/queries';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { DEFAULT_TRACKABLE_ITEM_TYPE_ICON } from '@/utils/imageUrl';
import { EditItemTypeCustomFields } from './EditItemTypeCustomFields';
import { itemTypeCategories, ItemTypeDetailModalMode } from './types';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/** Radix Select cannot hold an empty value, so "no billing category" needs a sentinel. */
const NO_BILLING_CATEGORY = 'NONE';

// One set of default event prices per Zoho Books organisation currency.
const EVENT_PRICE_CURRENCIES = [
  ItemTypeEventPriceCurrency.Eur,
  ItemTypeEventPriceCurrency.Usd,
] as const;

type DefaultEventPrices = Record<
  ItemTypeEventPriceCurrency,
  {
    rental_price: number | null;
    lost_price: number | null;
    cleaning_price: number | null;
  }
>;

function readDefaultEventPrices(
  prices: ItemTypeFormState['default_event_prices'] | undefined,
): DefaultEventPrices {
  const result = {} as DefaultEventPrices;
  for (const currency of EVENT_PRICE_CURRENCIES) {
    const entry = prices?.find((p) => p.currency === currency);
    result[currency] = {
      rental_price: entry?.rental_price ?? null,
      lost_price: entry?.lost_price ?? null,
      cleaning_price: entry?.cleaning_price ?? null,
    };
  }
  return result;
}

function toDefaultEventPriceInputs(
  prices: DefaultEventPrices,
): ItemTypeDefaultEventPriceInput[] {
  return EVENT_PRICE_CURRENCIES.map((currency) => ({
    currency,
    rental_price: prices[currency].rental_price,
    lost_price: prices[currency].lost_price,
    cleaning_price: prices[currency].cleaning_price,
  }));
}

function sameDefaultEventPrices(a: DefaultEventPrices, b: DefaultEventPrices) {
  return EVENT_PRICE_CURRENCIES.every(
    (c) =>
      a[c].rental_price === b[c].rental_price &&
      a[c].lost_price === b[c].lost_price &&
      a[c].cleaning_price === b[c].cleaning_price,
  );
}

function normalizeSku(sku: string | null | undefined): string | null {
  const trimmed = (sku ?? '').trim().toUpperCase();
  return trimmed === '' ? null : trimmed;
}

/** Cents input with a formatted preview, like the store price field. */
function CentsField(props: {
  id: string;
  label: string;
  currency: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  const { id, label, currency, value, onChange } = props;
  return (
    <Field className="tw:flex-1">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="tw:flex tw:gap-2 tw:items-center">
        <Input
          id={id}
          type="number"
          step="1"
          min="0"
          value={value ?? ''}
          onChange={(e) => {
            if (e.target.value === '') {
              onChange(null);
              return;
            }
            const parsed = parseInt(e.target.value);
            onChange(Number.isNaN(parsed) ? null : Math.max(0, parsed));
          }}
          className="tw:flex-1"
        />
        {value != null && (
          <span className="tw:text-sm tw:text-muted-foreground tw:whitespace-nowrap">
            {centsToPrice(value, currency)}
          </span>
        )}
      </div>
    </Field>
  );
}

const billingCategories = Object.values(BillingCategory);

function sanitizeCustomFieldDefinitions(
  fields:
    | ItemType['custom_field_definitions']
    | CustomFieldDefinitionInput[]
    | null
    | undefined,
): CustomFieldDefinitionInput[] {
  if (!fields?.length) return [];

  return fields.map((field) => ({
    id: field.id,
    name: field.name,
    type: field.type,
    required: field.required ?? undefined,
    default_value: field.default_value ?? undefined,
    depends_on: field.depends_on
      ? {
          field_id: field.depends_on.field_id,
          value: field.depends_on.value,
        }
      : undefined,
  }));
}

export default function ItemTypeDetailModal(props: {
  open: boolean;
  setOpen: (open: boolean) => void;
  itemType: ItemTypeFormState;
  setItemType: (itemType: ItemTypeFormState) => void;
  mode: ItemTypeDetailModalMode;
}) {
  const { open, setOpen, itemType, mode, setItemType } = props;

  const [price, setPrice] = useState<string>(
    itemType.price !== null && itemType.price !== undefined
      ? (itemType.price / 100).toString()
      : '',
  );
  const [weight, setWeight] = useState<string>(
    itemType.weight !== null && itemType.weight !== undefined
      ? itemType.weight.toString()
      : '0',
  );
  const [billingFrequency, setBillingFrequency] = useState<BillingFrequency>(
    itemType.billing_frequency || BillingFrequency.Monthly,
  );
  const [billomatArticles, setBillomatArticles] = useState<
    Array<{ country_code: string; article_id: number }>
  >([]);
  const [billomatArticlesCollapsed, setBillomatArticlesCollapsed] =
    useState(false);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<
    CustomFieldDefinitionInput[]
  >([]);
  const [sku, setSku] = useState<string>(itemType.sku ?? '');
  const [skuError, setSkuError] = useState<string | null>(null);
  const [defaultEventPrices, setDefaultEventPrices] =
    useState<DefaultEventPrices>(() =>
      readDefaultEventPrices(itemType.default_event_prices),
    );
  // What the type looked like when the modal opened, to decide whether a save
  // changed anything Zoho Books needs to hear about.
  const zohoBaseline = useRef<{
    sku: string | null;
    name: string;
    archived: boolean;
    imageUrl: string | null;
    prices: DefaultEventPrices;
  } | null>(null);

  const { data: countriesData = { __type: { enumValues: [] } } } = useQuery(
    allUserCountriesQuery,
  );
  const countries =
    countriesData.__type?.enumValues?.map((country: any) => country.name) || [];

  // Get available countries (countries not already used)
  const availableCountries = countries.filter(
    (country: string) =>
      !billomatArticles.some((article) => article.country_code === country),
  );

  useEffect(() => {
    if (itemType.price !== null && itemType.price !== undefined) {
      setPrice(itemType.price.toString());
    } else {
      setPrice('');
    }
    if (itemType.weight !== null && itemType.weight !== undefined) {
      setWeight(itemType.weight.toString());
    } else {
      setWeight('0');
    }
    if (itemType.billing_frequency) {
      setBillingFrequency(itemType.billing_frequency);
    } else {
      setBillingFrequency(BillingFrequency.Monthly);
    }
    if (itemType.billomat_article_ids_by_country) {
      setBillomatArticles(
        itemType.billomat_article_ids_by_country.map((item) => ({
          country_code: item.country_code,
          article_id: item.article_id,
        })),
      );
    } else {
      setBillomatArticles([]);
    }
    if (itemType.custom_field_definitions) {
      setCustomFieldDefinitions(
        sanitizeCustomFieldDefinitions(itemType.custom_field_definitions),
      );
    } else {
      setCustomFieldDefinitions([]);
    }
    setSku(itemType.sku ?? '');
    setDefaultEventPrices(
      readDefaultEventPrices(itemType.default_event_prices),
    );
    setSkuError(null);
  }, [itemType]);

  useEffect(() => {
    if (open) {
      zohoBaseline.current = {
        sku: normalizeSku(itemType.sku),
        name: itemType.name,
        archived: itemType.archived,
        imageUrl: itemType.image_url ?? null,
        prices: readDefaultEventPrices(itemType.default_event_prices),
      };
    }
    // The baseline is the type as it was when the dialog opened, nothing later.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function onSaveError(error: { message: string }) {
    if (/duplicate sku/i.test(error.message)) {
      setSkuError(i18n.t('ItemType:duplicateSkuError'));
      return;
    }
    LayoutStoreInstance.setError(error.message);
  }

  const [create, { loading: creating }] = useCreateItemTypeMutation({
    onError: onSaveError,
    refetchQueries: ['AllItemTypes'],
  });

  const [update, { loading: updating }] = useUpdateItemTypeMutation({
    onError: onSaveError,
    refetchQueries: ['AllItemTypes'],
  });

  const [syncItemTypesToZoho] = useSyncItemTypesToZohoMutation({
    onError: (error) => {
      LayoutStoreInstance.setError(
        i18n.t('ItemType:zohoSyncFailed') + error.message,
      );
    },
  });
  const [requestUploadUrlQuery] = useMutation(requestUploadUrl);

  async function uploadImage(e: any) {
    const { files } = e.target;
    const file = (files || [])[0];
    if (file) {
      const { type: filetype } = file;
      const uniqueFilename = `${randomId(6)}_itemType_image_${itemType.id}`;
      const { data } = await requestUploadUrlQuery({
        variables: { filename: uniqueFilename, filetype },
      });
      if (data.requestUploadUrl) {
        const url = await uploadToS3(file, data.requestUploadUrl);
        setItemType({
          ...itemType,
          image_url: url,
        });
      }
    }
  }

  function openFileSelector() {
    const input = window.document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => uploadImage(e);
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  function addBillomatArticle() {
    if (availableCountries.length > 0) {
      setBillomatArticles([
        ...billomatArticles,
        { country_code: availableCountries[0], article_id: 0 },
      ]);
    }
  }

  function removeBillomatArticle(index: number) {
    setBillomatArticles(billomatArticles.filter((_, i) => i !== index));
  }

  function updateBillomatArticle(
    index: number,
    field: 'country_code' | 'article_id',
    value: string | number,
  ) {
    const updated = [...billomatArticles];
    updated[index] = { ...updated[index], [field]: value };
    setBillomatArticles(updated);
  }

  async function handleSave() {
    let priceNumber: number | undefined = undefined;
    const sanitizedCustomFieldDefinitions = sanitizeCustomFieldDefinitions(
      customFieldDefinitions,
    );

    if (!isNullOrWhitespace(price)) {
      const priceTemp = parseInt(price);
      if (!isNaN(priceTemp) && priceTemp >= 0) {
        priceNumber = priceTemp;
      }
    }

    const weightNumber = parseInt(weight);
    if (isNullOrWhitespace(weight) || isNaN(weightNumber) || weightNumber < 0) {
      LayoutStoreInstance.setError(i18n.t('ItemType:weightIsRequired'));
      return;
    }

    if (mode === 'create' && isNullOrWhitespace(itemType.category)) {
      LayoutStoreInstance.setError(i18n.t('ItemType:categoryIsRequired'));
      return;
    }

    const normalizedSku = normalizeSku(sku);
    const eventPriceFields = {
      sku: normalizedSku,
      default_event_prices: toDefaultEventPriceInputs(defaultEventPrices),
    };
    const baseline = zohoBaseline.current;
    // Saving queues the Zoho Books sync on the backend when these change; the
    // explicit call here is what tells the user it happened.
    const zohoRelevantChanged =
      mode === 'create'
        ? normalizedSku != null
        : baseline == null ||
          baseline.sku !== normalizedSku ||
          baseline.name !== itemType.name ||
          baseline.archived !== itemType.archived ||
          baseline.imageUrl !== (itemType.image_url ?? null) ||
          !sameDefaultEventPrices(baseline.prices, defaultEventPrices);

    let savedId: number | null | undefined;
    if (mode === 'update') {
      if (isNullOrWhitespace(price)) {
        LayoutStoreInstance.setError(i18n.t('ItemType:priceIsRequired'));
        return;
      }

      const result = await update({
        variables: {
          id: itemType.id,
          update: {
            name: itemType.name,
            color_hex_code: itemType.color_hex_code,
            image_url: itemType.image_url,
            archived: itemType.archived,
            billing_category: itemType.billing_category ?? null,
            price: priceNumber !== undefined ? priceNumber : undefined,
            weight: weightNumber,
            billing_frequency: billingFrequency,
            billomat_article_ids_by_country:
              billomatArticles.length > 0
                ? billomatArticles.map((item) => ({
                    country_code: item.country_code,
                    article_id: item.article_id,
                  }))
                : undefined,
            custom_field_definitions: sanitizedCustomFieldDefinitions,
            ...eventPriceFields,
          },
        },
      });
      savedId = result.data?.updateItemType?.id;
    } else {
      const result = await create({
        variables: {
          create: {
            name: itemType.name,
            color_hex_code: itemType.color_hex_code,
            category: itemType.category as ItemTypeCategory,
            billing_category: itemType.billing_category ?? null,
            image_url: itemType.image_url,
            price: priceNumber !== undefined ? priceNumber : undefined,
            weight: weightNumber,
            billing_frequency: billingFrequency,
            custom_field_definitions: sanitizedCustomFieldDefinitions,
            ...eventPriceFields,
          },
        },
      });
      savedId = result.data?.createItemType?.id;
    }
    // The mutation hooks report failures through onError, so a missing id means
    // the save did not happen; keep the dialog open with the error shown.
    if (savedId == null) {
      return;
    }
    if (zohoRelevantChanged && normalizedSku != null) {
      await syncItemTypesToZoho({ variables: { ids: [savedId] } });
    }
    LayoutStoreInstance.setSuccess(
      i18n.t(
        zohoRelevantChanged && normalizedSku != null
          ? 'ItemType:savedZoho'
          : 'ItemType:saved',
      ),
    );
    setOpen(false);
  }

  return (
    <CustomModal
      acceptButtonText={
        mode === 'update' ? i18n.t('Common:save') : i18n.t('ItemType:create')
      }
      isOpen={open}
      setIsOpen={setOpen}
      header={
        mode === 'update'
          ? i18n.t('ItemType:editHeader')
          : i18n.t('ItemType:createHeader')
      }
      body={
        <FieldGroup className="tw:gap-4 tw:*:data-[slot=field]:gap-2">
          <Field>
            <FieldLabel htmlFor="item-type-name">
              {i18n.t('Common:name')}
            </FieldLabel>
            <Input
              id="item-type-name"
              type="text"
              onChange={(e) =>
                setItemType({ ...itemType, name: e.target.value })
              }
              value={itemType.name}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="item-type-sku">
              {i18n.t('ItemType:sku')}
            </FieldLabel>
            <Input
              id="item-type-sku"
              type="text"
              className="tw:font-mono"
              placeholder={i18n.t('ItemType:skuPlaceholder')}
              aria-invalid={skuError != null}
              value={sku}
              onChange={(e) => {
                setSkuError(null);
                setSku(e.target.value.toUpperCase());
              }}
            />
            {skuError ? (
              <FieldError>{skuError}</FieldError>
            ) : (
              <p
                className={
                  normalizeSku(sku) == null
                    ? 'tw:text-xs tw:text-amber-600'
                    : 'tw:text-xs tw:text-muted-foreground'
                }
              >
                {i18n.t('ItemType:skuHint')}
              </p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="item-type-color">
              {i18n.t('ItemType:colorHexCode')}
            </FieldLabel>
            <div className="tw:flex tw:gap-2 tw:items-center">
              <input
                id="item-type-color"
                type="color"
                className="tw:h-9 tw:w-12 tw:rounded-md tw:border tw:border-input tw:cursor-pointer"
                value={`#${itemType.color_hex_code || 'FFFFFF'}`}
                onChange={(e) =>
                  setItemType({
                    ...itemType,
                    color_hex_code: e.target.value.replace('#', ''),
                  })
                }
              />
              <Input
                type="text"
                className="tw:flex-1"
                value={itemType.color_hex_code || 'FFFFFF'}
                onChange={(e) => {
                  const hexValue = e.target.value
                    .replace(/[^0-9A-Fa-f]/g, '')
                    .slice(0, 6);
                  setItemType({
                    ...itemType,
                    color_hex_code: hexValue,
                  });
                }}
              />
            </div>
          </Field>

          <Field>
            <FieldLabel htmlFor="item-type-category">
              {i18n.t('ItemType:category')}
            </FieldLabel>
            <Select
              disabled={mode === 'update'}
              onValueChange={(value) =>
                setItemType({
                  ...itemType,
                  category: value as ItemTypeCategory,
                })
              }
              value={itemType.category}
            >
              <SelectTrigger id="item-type-category" className="tw:w-full">
                <SelectValue
                  placeholder={i18n.t('ItemType:categoryPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {itemTypeCategories
                  .filter((c) => c !== 'ALL')
                  .map((c) => (
                    <SelectItem key={c} value={c}>
                      {i18n.t(`ItemType:category${c}`)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="item-type-billing-category">
              {i18n.t('ItemType:billingCategory')}
            </FieldLabel>
            <Select
              onValueChange={(value) =>
                setItemType({
                  ...itemType,
                  billing_category:
                    value === NO_BILLING_CATEGORY
                      ? null
                      : (value as BillingCategory),
                })
              }
              value={itemType.billing_category ?? NO_BILLING_CATEGORY}
            >
              <SelectTrigger
                id="item-type-billing-category"
                className="tw:w-full"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_BILLING_CATEGORY}>
                  {i18n.t('ItemType:billingCategoryNONE')}
                </SelectItem>
                {billingCategories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {i18n.t(`ItemType:billingCategory${c}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="tw:text-xs tw:text-muted-foreground">
              {i18n.t('ItemType:billingCategoryHint')}
            </p>
          </Field>

          <Field>
            <FieldLabel htmlFor="item-type-archived">
              {i18n.t('ItemType:archived')}
            </FieldLabel>
            <Select
              disabled={mode === 'create'}
              value={itemType.archived.toString()}
              onValueChange={(value) =>
                setItemType({
                  ...itemType,
                  archived: value === 'true',
                })
              }
            >
              <SelectTrigger id="item-type-archived" className="tw:w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="false">{i18n.t('Common:no')}</SelectItem>
                <SelectItem value="true">{i18n.t('Common:yes')}</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>{i18n.t('ItemType:imageUrl')}</FieldLabel>
            <div
              onClick={openFileSelector}
              className="tw:cursor-pointer tw:rounded-md tw:border tw:border-input tw:p-2 tw:w-fit! tw:hover:bg-muted/50 tw:transition-colors"
            >
              <img
                className="tw:h-24 tw:w-24 tw:object-cover tw:rounded"
                src={itemType.image_url || DEFAULT_TRACKABLE_ITEM_TYPE_ICON}
                alt={itemType.name}
              />
            </div>
          </Field>

          <Field>
            <FieldLabel htmlFor="item-type-price">
              {i18n.t('ItemType:price')}
            </FieldLabel>
            <div className="tw:flex tw:gap-2 tw:items-center">
              <Input
                id="item-type-price"
                type="number"
                step="0.01"
                min="0"
                onChange={(e) => setPrice(e.target.value)}
                value={price}
                placeholder={i18n.t('ItemType:pricePlaceholder')}
                className="tw:flex-1"
              />
              {price && !isNaN(parseFloat(price)) && (
                <span className="tw:text-sm tw:text-muted-foreground tw:whitespace-nowrap">
                  {centsToPrice(Math.round(parseInt(price)), 'EUR')}
                </span>
              )}
            </div>
          </Field>

          <div className="tw:flex tw:flex-col tw:gap-2">
            <span className="tw:font-semibold">
              {i18n.t('ItemType:eventPrices')}
            </span>
            <p className="tw:text-xs tw:text-muted-foreground">
              {i18n.t('ItemType:eventPricesHint')}
            </p>
            {EVENT_PRICE_CURRENCIES.map((currency) => (
              <div key={currency} className="tw:flex tw:gap-4 tw:items-end">
                <span className="tw:w-10 tw:pb-2 tw:text-sm tw:font-medium">
                  {currency}
                </span>
                <CentsField
                  id={`item-type-event-rent-${currency}`}
                  label={i18n.t('ItemType:eventPriceRent')}
                  currency={currency}
                  value={defaultEventPrices[currency].rental_price}
                  onChange={(value) =>
                    setDefaultEventPrices({
                      ...defaultEventPrices,
                      [currency]: {
                        ...defaultEventPrices[currency],
                        rental_price: value,
                      },
                    })
                  }
                />
                <CentsField
                  id={`item-type-event-loss-${currency}`}
                  label={i18n.t('ItemType:eventPriceLoss')}
                  currency={currency}
                  value={defaultEventPrices[currency].lost_price}
                  onChange={(value) =>
                    setDefaultEventPrices({
                      ...defaultEventPrices,
                      [currency]: {
                        ...defaultEventPrices[currency],
                        lost_price: value,
                      },
                    })
                  }
                />
                <CentsField
                  id={`item-type-event-cleaning-${currency}`}
                  label={i18n.t('ItemType:eventPriceCleaning')}
                  currency={currency}
                  value={defaultEventPrices[currency].cleaning_price}
                  onChange={(value) =>
                    setDefaultEventPrices({
                      ...defaultEventPrices,
                      [currency]: {
                        ...defaultEventPrices[currency],
                        cleaning_price: value,
                      },
                    })
                  }
                />
              </div>
            ))}
          </div>

          <Field>
            <FieldLabel htmlFor="item-type-weight">
              {i18n.t('ItemType:weight')}
            </FieldLabel>
            <Input
              id="item-type-weight"
              type="number"
              step="1"
              min="0"
              onChange={(e) => setWeight(e.target.value)}
              value={weight}
              placeholder={i18n.t('ItemType:weightPlaceholder')}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="item-type-billing-frequency">
              {i18n.t('ItemType:billingFrequency')}
            </FieldLabel>
            <Select
              onValueChange={(value) =>
                setBillingFrequency(value as BillingFrequency)
              }
              value={billingFrequency}
            >
              <SelectTrigger
                id="item-type-billing-frequency"
                className="tw:w-full"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BillingFrequency.OneTime}>
                  {i18n.t('ItemType:billingFrequencyOneTime')}
                </SelectItem>
                <SelectItem value={BillingFrequency.Monthly}>
                  {i18n.t('ItemType:billingFrequencyMonthly')}
                </SelectItem>
                <SelectItem value={BillingFrequency.Yearly}>
                  {i18n.t('ItemType:billingFrequencyYearly')}
                </SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {(itemType.category === 'ASSET' ||
            itemType.category === 'STORE_ASSET') && (
            <Field>
              <EditItemTypeCustomFields
                value={customFieldDefinitions}
                onChange={setCustomFieldDefinitions}
                editable
                lockedFieldIds={
                  mode === 'update'
                    ? new Set(
                        (itemType.custom_field_definitions ?? []).map(
                          (f) => f.id,
                        ),
                      )
                    : undefined
                }
              />
            </Field>
          )}

          {mode === 'update' && (
            <Collapsible
              open={!billomatArticlesCollapsed}
              onOpenChange={(open) => setBillomatArticlesCollapsed(!open)}
            >
              <div className="tw:flex tw:justify-between tw:items-center tw:mb-2">
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="tw:gap-2 tw:px-0 tw:hover:bg-transparent"
                  >
                    <span className="tw:font-semibold">
                      {i18n.t('ItemType:billomatArticles')}
                    </span>
                    {billomatArticlesCollapsed ? (
                      <ChevronDown className="tw:size-4" />
                    ) : (
                      <ChevronUp className="tw:size-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <Button
                  size="sm"
                  onClick={addBillomatArticle}
                  disabled={availableCountries.length === 0}
                >
                  <FaPlus className="tw:mr-1" />
                  {i18n.t('ItemType:billomatArticlesAdd')}
                </Button>
              </div>
              <CollapsibleContent>
                {billomatArticles.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          {i18n.t('ItemType:billomatArticlesCountry')}
                        </TableHead>
                        <TableHead>
                          {i18n.t('ItemType:billomatArticlesArticleId')}
                        </TableHead>
                        <TableHead className="tw:w-20" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {billomatArticles.map((article, index) => {
                        const availableForRow = countries.filter(
                          (country: string) =>
                            country === article.country_code ||
                            !billomatArticles.some(
                              (a, i) =>
                                i !== index && a.country_code === country,
                            ),
                        );
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Select
                                value={article.country_code}
                                onValueChange={(value) =>
                                  updateBillomatArticle(
                                    index,
                                    'country_code',
                                    value,
                                  )
                                }
                              >
                                <SelectTrigger size="sm" className="tw:w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableForRow.map((country: string) => (
                                    <SelectItem key={country} value={country}>
                                      {i18n.t(`Common:Country:${country}`)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="tw:h-8"
                                value={article.article_id || ''}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value);
                                  if (!isNaN(value) && value >= 0) {
                                    updateBillomatArticle(
                                      index,
                                      'article_id',
                                      value,
                                    );
                                  } else if (e.target.value === '') {
                                    updateBillomatArticle(
                                      index,
                                      'article_id',
                                      0,
                                    );
                                  }
                                }}
                                min="0"
                                placeholder={i18n.t(
                                  'ItemType:billomatArticlesArticleId',
                                )}
                              />
                            </TableCell>
                            <TableCell className="tw:text-center">
                              <Button
                                variant="destructive"
                                size="icon-sm"
                                onClick={() => removeBillomatArticle(index)}
                              >
                                <FaTrash />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="tw:text-muted-foreground tw:text-sm">
                    {i18n.t('ItemType:billomatArticlesEmpty')}
                  </p>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </FieldGroup>
      }
      acceptButtonAction={handleSave}
      acceptButtonDisabled={
        itemType.name.trim().length < 3 || creating || updating
      }
      acceptButtonIcon={mode === 'update' ? <FaSave /> : <FaPlus />}
    />
  );
}
